import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import type {
  PlayerProfile,
  SlotDemoScene,
  SlotLayoutBounds,
  SlotSpinFn,
  SlotSymbolDef,
  Theme,
} from '@goodtime/engine';
import {
  ALL_ITEMS,
  ITEMS,
  WHEEL_MATCHES_PER_SESSION,
  WHEEL_PHASE_META,
  type BotArchetype,
  type ItemId,
  type WheelObservableState,
  type WheelSeatView,
} from '@goodtime/shared';
import { CanvasHost } from '../CanvasHost';
import { Icon } from '../Icon';
import { InviteFriendSheet } from './InviteFriendSheet';
import { ReactionFloat } from './ReactionFloat';
import { SeatAvatar } from './SeatAvatar';
import { SeatGestureButton } from './SeatGestureButton';
import { submitWheelCommand } from '../../api/wheelCommand';
import type { ReceivedReaction } from '../../hooks/useWheelRoomStream';

/** Empty default so callers can omit the prop without us allocating a
 *  fresh array on every render (which would defeat React's prop-stable
 *  memoisation in SeatTile's reaction filter). */
const EMPTY_REACTIONS: ReadonlyArray<ReceivedReaction> = [];
import bonusItemUrl from '../../assets/reel/forest/item_drop.png';

const REEL_RACE_DURATION_MS = (WHEEL_PHASE_META.REEL_RACE.durationSec ?? 60) * 1000;

/**
 * Item-icon URL lookup, keyed by {@link ItemId}. Vite's
 * {@code import.meta.glob} discovers every PNG in
 * {@code apps/mobile/src/assets/item/} at build time and exposes the
 * URLs synchronously (eager). Filename lowercased matches the lower-
 * case ItemId convention used by the toolgen pipeline; we upper-case
 * to match the enum exactly. Used by {@link SelfItemDropReveal} to
 * fly the actual item icon from the reel cell to the BalancePill —
 * producer brief 2026-05-13: "the item symbol that flies to the
 * hud, not the label."
 */
const ITEM_ICON_MODULES = import.meta.glob<{ default: string }>(
  '../../assets/item/*.png',
  { eager: true },
);
const ITEM_ICON_URL_BY_ID: Partial<Record<ItemId, string>> = Object.fromEntries(
  Object.entries(ITEM_ICON_MODULES)
    .map(([path, mod]) => {
      const filename = path.split('/').pop()?.replace(/\.png$/, '');
      if (!filename) return null;
      return [filename.toUpperCase() as ItemId, mod.default] as const;
    })
    .filter((entry): entry is readonly [ItemId, string] => entry !== null),
);

/** Per-token-delta animation: a `+N` floats up from the avatar.
 * Lifetime ~900ms. Fired for any seat (self + bots) whose tokens
 * increased between SSE deltas. */
interface CollectBurst {
  id: string;
  delta: number;
  endX: number;
  endY: number;
  bornMs: number;
}

interface ItemBurst {
  id: string;
  /** Start (viewport coords) — bot avatar centre. The legacy
   *  "simple particle" path used only for BOT drops now; SELF drops
   *  go through {@link SelfItemDropReveal} instead. */
  startX: number;
  startY: number;
  /** End (viewport coords) — bot avatar centre (the particle pops at
   *  the avatar with a small drift). Kept for parity with the older
   *  signature; the new visual is a single-anchor pop. */
  endX: number;
  endY: number;
  bornMs: number;
}

/** Self-seat item-drop reveal. Producer brief 2026-05-12: "ideally
 *  the star lands, shows for a second, then reveals the special
 *  item". Drives a 3-phase overlay anchored to the cell where the
 *  server says the item dropped:
 *
 *  <ol>
 *    <li>Star pops in at the cell (~0.3s).</li>
 *    <li>Linger as a glowing star (~1s).</li>
 *    <li>Cross-fade to a labelled item chip (BOOST / SHIELD / etc.)
 *        and arc it to the BalancePill items chip (~1.5s).</li>
 *  </ol> */
interface ItemDropReveal {
  id: string;
  /** 0..14 cell index — drives the start (cell screen position). */
  cell: number;
  /** The specific item that dropped. */
  itemId: ItemId;
  /** Slot canvas bounds at drop time — cell screen position is
   *  computed from these fractions. Captured once at drop so the
   *  reveal doesn't drift if the canvas re-lays-out mid-animation. */
  canvasRect: { left: number; top: number; width: number; height: number };
  /** End point (viewport coords) — where the labelled chip flies
   *  to. Usually the BalancePill items chip. */
  endX: number;
  endY: number;
  bornMs: number;
}

/** Vertical pitch (px) between adjacent rail seats in the desktop /
 *  wide-viewport right-side stack. Drives the absolute-positioning
 *  layout that lets us animate rank changes via `transition: top`
 *  instead of having flex layout jump. */
const SEAT_RAIL_PITCH = 70;
/** Pixels reserved beneath the last seat for the invite slot in the
 *  desktop stack layout. */
const INVITE_SLOT_OFFSET = 14;
/** Grid columns for the mobile bottom-anchored seat rail. 6 player
 *  seats + 1 invite chip fit a 4-wide / 2-row layout: row 1 =
 *  ranks 1–4, row 2 = ranks 5–6 + invite. */
const MOBILE_RAIL_COLS = 4;
/** Horizontal pitch (px) between adjacent mobile-rail cells,
 *  accounting for the rank/token badges that overflow ±4px each
 *  side. */
const MOBILE_RAIL_PITCH_X = 64;
/** Vertical pitch (px) between mobile-rail rows — needs a touch
 *  more breathing room than columns so the token-count badge
 *  doesn't clip the row beneath. */
const MOBILE_RAIL_PITCH_Y = 68;
/** Viewport-width breakpoint (px) below which the seat rail uses
 *  the mobile bottom layout. Producer brief 2026-05-11: the
 *  bottom-rows layout is mobile-only — wider viewports keep the
 *  side rail so the slot frame can centre against the page. */
const MOBILE_RAIL_BREAKPOINT_PX = 640;
/** Gap (px) the mobile rail keeps below the viewport bottom — lifts it
 *  clear of the room-level reaction pill (bottom:18, ~50px tall). The
 *  rail's BOTTOM edge stays anchored here; the responsive down-scale
 *  (below) only pulls the TOP edge down, so this clearance is
 *  preserved at every scale. */
const MOBILE_RAIL_BOTTOM_INSET = 72;

// ---- mobile rail ↔ SPIN-button clearance --------------------------------
// The slot's SPIN disc is centred on the BET/SPIN/INFO row whose Y the
// scene reports as {@link SlotLayoutBounds#spinButtonY}. The disc's
// painted lower edge reaches ~this far below that centreline at the
// mobile HUD scale (disc half-height − the disc's "lift" into the wood
// frame; both scale with the slot HUD, which is pinned to its minimum
// 0.55 ratio for every phone-width viewport). On short viewports the
// centred slot's SPIN disc descends toward the bottom-anchored rail; we
// shrink the rail just enough that its top row never rides up under the
// disc. Producer flag 2026-06-01.
const SPIN_DISC_BELOW_ROW_PX = 24;
/** Clear vertical gap to leave between the SPIN disc's lower edge and the
 *  rail's top row. */
const RAIL_SPIN_GAP_PX = 12;
/** Don't shrink the rail past this — below it the avatars/badges stop
 *  being legible, and we'd rather accept a tiny disc graze on
 *  pathologically short viewports than an unreadable leaderboard. At
 *  this floor the rail fully clears the SPIN disc down to ~630px tall,
 *  which covers every standard portrait phone (incl. iPhone SE at 667)
 *  plus a safe-area/split-screen margin; shorter than that degrades to
 *  a small graze rather than an unreadable rail. */
const RAIL_MIN_SCALE = 0.72;

function clampRailScale(value: number): number {
  return Math.max(RAIL_MIN_SCALE, Math.min(1, value));
}

/** How long to keep the strip hidden after a spin starts.
 *  slotSpinFn resolves on SSE-delta arrival (often well under 500
 *  ms), but the slot scene's reel-deceleration animation takes
 *  another ~1.5 s on top. Without this hold the strip pops back in
 *  mid-reel-spin and reads as a flash. The Pixi-side fade runs
 *  through this whole window. */
const STRIP_HIDDEN_HOLD_MS = 1600;


export interface WheelReelRaceViewProps {
  state: WheelObservableState;
  /** Server-side seat ids the local player owns — used to bias the
   * leaderboard highlight. Pass null/empty when no auth yet. */
  selfSeatId?: number | null;
  /** Async handler bound to the local player's REEL_RACE_SPIN
   * submission. Null = no spin button (audience mode). */
  onSpin?: (() => Promise<void>) | null;
  /** Pixi theme — drives the actual slot scene's visuals. Falls back
   * to a default if not supplied (renders a neutral grid). Already
   * reflects the player's chosen reel-skin when one is set. */
  theme?: Theme | null;
  /** @deprecated Reel-skin selection moved into the Settings dialog
   *  (producer 2026-06-08). Still accepted + forwarded by the host but
   *  no longer rendered here — the whole pass-through chain (App →
   *  WheelLobbyView → here) can be dropped in a later cleanup. */
  availableSkins?: readonly { id: string; name: string; iconUrl?: string }[];
  selectedSkinId?: string;
  onSelectSkin?: (themeId: string) => void;
  /** Player profile — owns wallet balance shown in the slot HUD. */
  profile?: PlayerProfile | null;
  /** Fired once per slot-scene spin commit — used by the parent to
   * submit a REEL_RACE_SPIN command to the server so the local
   * player's slot spins translate into multiplayer tokens. */
  onSlotSpinFired?: ((bet: number) => void) | null;
  /** Server-authoritative slot spin function. When provided, the slot
   * scene calls this on every spin and awaits the returned grid; the
   * parent's bridge submits REEL_RACE_SPIN + waits for the SSE delta
   * carrying the seat's lastSpinGrid. Replaces onSlotSpinFired (the
   * spinFn does the submit itself). */
  slotSpinFn?: SlotSpinFn | null;
  /** F2.2 — onboarding-session number for the local player. Threads
   *  through to the embedded slot scene so SlotHudComponent can lock
   *  BET to 25 during session 1 (UX Spec §4.1 — first match must
   *  guarantee a non-trivial payout). Null = no curriculum gating. */
  onboardingSession?: number | null;
  /** Tap-target for the gear icon on the slot HUD's INFO + SETTINGS
   *  cluster. Threaded through to the embedded CanvasHost so the
   *  existing button surface in the wheel-room slot becomes
   *  interactive (it's a no-op when this is null). */
  onOpenSettings?: (() => void) | null;
  /** Mirrors the slot's info-modal (paytable / items) open state up to
   *  the parent, which uses it to hide the floating BalancePill — that
   *  pill is a React overlay that would otherwise paint above the
   *  canvas-rendered modal, exactly like the local overlays gated on
   *  {@code infoModalOpen}. */
  onInfoModalOpenChange?: (open: boolean) => void;
  /** Room id — used to submit CLAIM_ITEM when a drop lands on a full
   *  loadout and the player taps a slot to swap. Null disables the
   *  claim UI (audience / pre-join). */
  roomId?: string | null;
  /** Local player UUID — paired with {@link roomId} for command
   *  submission. Null disables the claim UI. */
  playerId?: string | null;
  /** R1.4 — live ring buffer of recent reaction events from
   *  {@link useWheelRoomStream}. Forwarded to each seat tile so the
   *  float-up emoji overlay anchors to the matching avatar. Pass an
   *  empty array (or omit) to disable the overlay. */
  reactions?: ReadonlyArray<ReceivedReaction>;
  /** Social MVP — the room's shareable invite code (e.g. "GTC-4F2A").
   *  When set, the seat-rail "+" (InviteAvatarButton) copies it to the
   *  clipboard so the host can share the table. Null hides the copy
   *  affordance (the "+" stays as a plain placeholder). */
  inviteCode?: string | null;
  /** U11 cast favourites — when set, tapping a bot seat's avatar in the
   *  rail invokes this callback with the seat's archetype. The lobby
   *  opens a {@code ProfileCard} for the tapped archetype. Player seats
   *  (kind === 'PLAYER') don't carry an archetype and stay inert. Pass
   *  {@code null} to disable the affordance (audience-mode / pre-join
   *  views). Same prop shape as {@link WheelPayoutView}. */
  onArchetypeTap?: ((archetype: BotArchetype) => void) | null;
  /** When true (the production default), the mobile bottom rail
   *  responsively shrinks on short viewports so its top row never rides
   *  up under the slot's SPIN disc — see {@link railScale}. The R4
   *  capture preview sets this {@code false} because it applies its own
   *  footage-only layout accommodation (the {@code .rr-scale} hack) and
   *  must not have its rail geometry altered by the product fix. */
  responsiveRail?: boolean;
}

/**
 * U4 Reel Race screen. Renders during the REEL_RACE phase. Leaderboard
 * on the left rail, 5×3 forest-themed grid in the centre, countdown
 * across the top.
 *
 * <p>The grid is a placeholder visual: nine forest symbols cycle every
 * ~280ms, matching the spec's "constant motion to feel alive" cue.
 * The full canvas slot scene from {@code packages/engine/scenes} is
 * the upgrade target — wires in once the Pixi/React boundary is
 * cleaner. The MVP bar per the master plan is "ugly but functional";
 * this delivers that.
 *
 * <p>The countdown is locally driven from the moment the view first
 * sees REEL_RACE phase. The server doesn't yet publish a phase-end
 * timestamp on the observable; if the phase changes server-side
 * before our local timer expires, the parent unmounts this view and
 * the timer is reset on next REEL_RACE entry.
 */
export function WheelReelRaceView({
  state,
  selfSeatId = null,
  onSpin = null,
  theme = null,
  profile = null,
  onSlotSpinFired = null,
  slotSpinFn = null,
  onboardingSession = null,
  onOpenSettings = null,
  onInfoModalOpenChange,
  roomId = null,
  playerId = null,
  reactions = EMPTY_REACTIONS,
  inviteCode = null,
  onArchetypeTap = null,
  responsiveRail = true,
}: WheelReelRaceViewProps) {
  const phaseStartRef = useRef<number>(Date.now());
  const [now, setNow] = useState<number>(Date.now());
  const [spinPending, setSpinPending] = useState(false);

  useEffect(() => {
    phaseStartRef.current = Date.now();
    const id = window.setInterval(() => setNow(Date.now()), 200);
    return () => {
      window.clearInterval(id);
    };
  }, []);

  // Mobile = narrow viewport (≤ {@link MOBILE_RAIL_BREAKPOINT_PX}px).
  // Drives the seat-rail layout swap: bottom 4×2 grid on phones (so
  // the rail doesn't block the reels) vs the right-side vertical
  // stack on tablet+ where there's lateral room beside the slot frame.
  const [isMobileRail, setIsMobileRail] = useState<boolean>(() =>
    typeof window === 'undefined'
      ? false
      : window.innerWidth <= MOBILE_RAIL_BREAKPOINT_PX,
  );
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mql = window.matchMedia(
      `(max-width: ${MOBILE_RAIL_BREAKPOINT_PX}px)`,
    );
    const onChange = () => setIsMobileRail(mql.matches);
    onChange();
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, []);

  // Slot scene's reported layout bounds — we read the SPIN-row centreline
  // and the canvas (= this view's) height to scale the mobile rail so it
  // never rides up under the SPIN disc on short viewports. Both values are
  // in the SAME CSS-px space as the bottom-anchored rail (the canvas fills
  // this view at inset:0), so no window/container reconciliation is needed.
  // Null until the first layout callback arrives (e.g. the no-canvas
  // FallbackReelGrid path in tests, where no scaling is needed).
  const [slotBounds, setSlotBounds] = useState<{
    spinRowY: number;
    height: number;
    machineTop: number;
    machineLeft: number;
    rendererWidth: number;
  } | null>(null);
  const handleSlotLayoutBounds = useCallback((b: SlotLayoutBounds) => {
    setSlotBounds((prev) =>
      prev &&
      prev.spinRowY === b.spinButtonY &&
      prev.height === b.rendererHeight &&
      prev.machineTop === b.machineTop &&
      prev.machineLeft === b.machineLeft &&
      prev.rendererWidth === b.rendererWidth
        ? prev
        : {
            spinRowY: b.spinButtonY,
            height: b.rendererHeight,
            machineTop: b.machineTop,
            machineLeft: b.machineLeft,
            rendererWidth: b.rendererWidth,
          },
    );
  }, []);

  // Responsive down-scale for the mobile bottom rail. The rail's BOTTOM
  // edge is anchored at {@link MOBILE_RAIL_BOTTOM_INSET}; this only pulls
  // its TOP down (by scaling the whole rail about its bottom-centre), so
  // the lower row keeps clearing the reaction pill while the upper row
  // drops clear of the SPIN disc. 1 (no scaling) on tall viewports and in
  // the desktop side-rail layout.
  const railScale = useMemo(() => {
    if (!responsiveRail || !isMobileRail || slotBounds === null) return 1;
    const naturalOlHeight = 2 * MOBILE_RAIL_PITCH_Y;
    const olBottomEdge = slotBounds.height - MOBILE_RAIL_BOTTOM_INSET;
    const railTopMin =
      slotBounds.spinRowY + SPIN_DISC_BELOW_ROW_PX + RAIL_SPIN_GAP_PX;
    const maxOlHeight = olBottomEdge - railTopMin;
    return clampRailScale(maxOlHeight / naturalOlHeight);
  }, [responsiveRail, isMobileRail, slotBounds]);

  const elapsedMs = now - phaseStartRef.current;
  const remainingMs = Math.max(0, REEL_RACE_DURATION_MS - elapsedMs);
  const remainingSec = Math.ceil(remainingMs / 1000);

  const leaderboard = useMemo(
    () => sortByTokens(state.seats),
    [state.seats],
  );

  // The local player's seat (if any) — drives the claim overlay when
  // a drop lands on a full loadout. Resolved here once so the JSX
  // doesn't have to scan {@code state.seats} repeatedly.
  const selfSeat = useMemo(
    () =>
      selfSeatId !== null
        ? state.seats.find((s) => s.seatId === selfSeatId) ?? null
        : null,
    [state.seats, selfSeatId],
  );

  // Track whether the local player is mid-spin so the loadout strip
  // can hide during the reel motion (producer brief: strip
  // "disappears when they press spin"). Wraps {@link slotSpinFn} so
  // every spin flips the flag for the lifetime of the returned
  // promise. The slot HUD inside the canvas owns spin triggering, so
  // wrapping at the spinFn boundary is the only DOM-side hook
  // available.
  //
  // <p>{@link stripHidden} extends past {@link slotSpinFn}'s resolve
  // by a fixed {@link STRIP_HIDDEN_HOLD_MS} window. slotSpinFn
  // resolves the moment the server's SSE delta with the new grid
  // arrives (often well under 500 ms), but the slot scene's reel
  // deceleration animation still takes ~1.5 s on top of that. Without
  // the hold, the strip flashes on and off between SSE arrival and
  // the reels actually landing.
  const [isSpinning, setIsSpinning] = useState(false);
  /** Producer brief 2026-05-15 — true while the slot scene's info
   *  modal (paytable / items tab) is open. Hides the React overlays
   *  (countdown, leaderboard, "watching" pill) that would otherwise
   *  paint above the canvas-rendered modal at zIndex 10+. */
  const [infoModalOpen, setInfoModalOpen] = useState(false);
  // Mirror the info-modal open state up to the parent (WheelLobbyView)
  // so it can hide the floating BalancePill — a React overlay that would
  // otherwise paint above the canvas-rendered modal, exactly like the
  // local overlays gated on {@link infoModalOpen} below.
  useEffect(() => {
    onInfoModalOpenChange?.(infoModalOpen);
  }, [infoModalOpen, onInfoModalOpenChange]);
  const [stripHidden, setStripHidden] = useState(false);
  const stripHoldTimerRef = useRef<number | null>(null);
  const trackedSlotSpinFn = useMemo<SlotSpinFn | null>(() => {
    if (!slotSpinFn) return null;
    return async (req) => {
      setIsSpinning(true);
      setStripHidden(true);
      if (stripHoldTimerRef.current !== null) {
        window.clearTimeout(stripHoldTimerRef.current);
      }
      stripHoldTimerRef.current = window.setTimeout(() => {
        setStripHidden(false);
        stripHoldTimerRef.current = null;
      }, STRIP_HIDDEN_HOLD_MS);
      try {
        return await slotSpinFn(req);
      } finally {
        setIsSpinning(false);
      }
    };
  }, [slotSpinFn]);
  useEffect(() => {
    return () => {
      if (stripHoldTimerRef.current !== null) {
        window.clearTimeout(stripHoldTimerRef.current);
      }
    };
  }, []);

  /** Stable SlotSymbolDef list for the cycle-and-settle reveal. One
   *  entry per ItemId, in catalog order. Texture aliases follow the
   *  {@code item:{ITEM_ID}} convention registered in
   *  {@code apps/mobile/src/themes/index.ts#SHARED_ITEM_ICONS}; the
   *  fallback emoji glyph is the placeholder from {@code items.ts}
   *  so the cell still shows *something* if the texture isn't loaded
   *  (e.g. before the asset-gen CLI has produced the commissioned
   *  PNGs). Memoised so the array identity stays stable across the
   *  hundreds of state ticks per match — the engine does
   *  reference-equality checks downstream. */
  const itemCycleDefs = useMemo<readonly SlotSymbolDef[]>(
    () =>
      ALL_ITEMS.map((id) => ({
        id: `item:${id}`,
        glyph: ITEMS[id].glyph,
        // Warm gold tint — only visible when the texture isn't loaded
        // and the emoji glyph fallback paints. Matches the bonus +
        // item_drop entries' palette.
        tint: 0xffd86b,
        weight: 0,
        textureAlias: `item:${id}`,
      })),
    [],
  );
  const itemDefById = useMemo(() => {
    const map = new Map<ItemId, SlotSymbolDef>();
    for (let i = 0; i < ALL_ITEMS.length; i++) {
      map.set(ALL_ITEMS[i]!, itemCycleDefs[i]!);
    }
    return map;
  }, [itemCycleDefs]);

  // ---- collect-burst animation ----------------------------------------
  // Watch every seat's token count. When any seat (self or bot) has
  // delta > 0 between SSE events, pop a small `+N` near that seat's
  // avatar. Bot deltas are sparse (0/1/2/3 bonuses per spin per the
  // bonus-token economy) so this stays readable at the room's tick
  // rate — no flying particles, just a local pop.
  const canvasWrapRef = useRef<HTMLDivElement>(null);

  const seatRefs = useRef<Map<number, HTMLLIElement>>(new Map());
  /** Previous token count per seat. `null` sentinel means "not yet
   * observed" — we never burst on the very first observation. */
  const prevTokensBySeatRef = useRef<Map<number, number>>(new Map());
  const [bursts, setBursts] = useState<CollectBurst[]>([]);
  /** Previous loadout SIZE per seat. Same first-observation rule —
   *  prevents a flash on initial mount when bots already have items. */
  const prevLoadoutSizeBySeatRef = useRef<Map<number, number>>(new Map());
  /** Previous {@link WheelSeatView#lastSpinPendingItem} per seat.
   *  Triggers a reveal animation when a drop lands on a full loadout
   *  (loadout size doesn't change in that case — the trigger is the
   *  pending field appearing). */
  const prevPendingBySeatRef = useRef<Map<number, ItemId | null>>(new Map());
  /** Self-only — how many loadout slots the player has visually
   *  "received" so far. Lags behind {@code selfSeat.loadout.length}
   *  until the engine's cycle-and-settle reveal completes, so the
   *  strip slot doesn't fill before the reveal lands on the reel.
   *  Producer brief 2026-05-14: "the slot shouldn't fill until the
   *  animation has finished and the item is revealed." */
  const [revealedSelfLoadoutCount, setRevealedSelfLoadoutCount] =
    useState<number>(0);
  /** Self-only — the pending-claim item that has finished its
   *  cycle-and-settle reveal and is ready to be shown in the
   *  "Tap a slot to replace" badge above the loadout strip. Lags
   *  behind {@code selfSeat.lastSpinPendingItem} for the same
   *  reason. Null until the reveal completes (or the player claims,
   *  whichever clears the server state first). */
  const [revealedSelfPendingItem, setRevealedSelfPendingItem] =
    useState<ItemId | null>(null);

  /** Stable asset-alias array (length 3) for the Pixi loadout strip.
   *  Each entry is {@code "item:<ID>"} or null for an empty slot.
   *  Reads from {@code selfSeat.loadout}, clamped to the revealed
   *  count so newly dropped items don't fill the slot before the
   *  on-reel reveal completes. */
  const loadoutSlotAliases = useMemo<ReadonlyArray<string | null>>(() => {
    const out: Array<string | null> = [null, null, null];
    if (!selfSeat) return out;
    const visibleCount = Math.min(revealedSelfLoadoutCount, selfSeat.loadout.length);
    for (let i = 0; i < visibleCount && i < 3; i++) {
      const id = selfSeat.loadout[i];
      if (id) out[i] = `item:${id}`;
    }
    return out;
  }, [selfSeat, revealedSelfLoadoutCount]);

  /** Tile-tap handler for the Pixi strip — fires CLAIM_ITEM with
   *  the chosen slot index. No-op outside an active pending state
   *  (the strip's tile buttons only fire when pending is set). */
  const handleClaimItemTile = useCallback(
    (slotIndex: number) => {
      if (!roomId || !playerId) return;
      void submitWheelCommand(roomId, 'CLAIM_ITEM', playerId, {
        slotIndex,
      }).catch((e) =>
        console.warn('[WheelReelRaceView] CLAIM_ITEM failed', e),
      );
    },
    [roomId, playerId],
  );
  const [itemBursts, setItemBursts] = useState<ItemBurst[]>([]);
  /** SlotDemoScene reference handed up by CanvasHost. Used to call
   *  {@code playItemDropReveal} on the engine when the server says
   *  a loadout item dropped on a self-seat spin — the cell-cycle
   *  reveal happens in Pixi at world scale rather than as a DOM
   *  overlay. {@code null} between mount transitions. */
  const slotSceneRef = useRef<SlotDemoScene | null>(null);

  useEffect(() => {
    const prevMap = prevTokensBySeatRef.current;
    const seenSeatIds = new Set<number>();
    const newBursts: CollectBurst[] = [];

    for (const seat of state.seats) {
      seenSeatIds.add(seat.seatId);
      const tokens = seat.tokens;
      const prev = prevMap.get(seat.seatId);
      if (prev === undefined) {
        // First observation for this seat — snapshot without
        // bursting. Avoids flashing pops on mount when the room
        // already has non-zero token counts.
        prevMap.set(seat.seatId, tokens);
        continue;
      }
      prevMap.set(seat.seatId, tokens);
      const delta = tokens - prev;
      if (delta <= 0) continue;

      const avatarEl = seatRefs.current.get(seat.seatId);
      if (!avatarEl) continue;
      const aRect = avatarEl.getBoundingClientRect();
      const nowMs = Date.now();

      newBursts.push({
        id: `${seat.seatId}-${nowMs}-${Math.random().toString(36).slice(2, 7)}`,
        delta,
        endX: aRect.left + aRect.width / 2,
        endY: aRect.top + aRect.height / 2,
        bornMs: nowMs,
      });
    }

    // Drop tracking for seats that left the room so they re-burst
    // cleanly if they rejoin later.
    for (const seatId of [...prevMap.keys()]) {
      if (!seenSeatIds.has(seatId)) prevMap.delete(seatId);
    }

    if (newBursts.length === 0) return;
    setBursts((b) => [...b, ...newBursts]);
    const ids = newBursts.map((b) => b.id);
    const timer = window.setTimeout(() => {
      setBursts((all) => all.filter((b) => !ids.includes(b.id)));
    }, 900);
    return () => window.clearTimeout(timer);
  }, [state.seats]);

  // Item drops — branches:
  //  - SELF seat: spawn a SelfItemDropReveal anchored to the cell
  //    server says the item landed on. Producer brief 2026-05-12:
  //    "ideally the star lands, shows for a second, then reveals
  //    the special item."
  //  - BOT seat: small star burst at the bot's avatar (legacy
  //    behavior, just enough to register "they got something").
  // Same first-observation rule as token bursts so we don't pop on
  // mount.
  useEffect(() => {
    const prevSizeMap = prevLoadoutSizeBySeatRef.current;
    const prevPendingMap = prevPendingBySeatRef.current;
    const seenSeatIds = new Set<number>();
    const newItemBursts: ItemBurst[] = [];

    // Slot canvas bounds — needed for self reveals (cell screen
    // position is computed as a fraction of the canvas rect).
    const slotCanvas = document.querySelector('canvas');
    const slotRect = slotCanvas?.getBoundingClientRect() ?? null;

    for (const seat of state.seats) {
      seenSeatIds.add(seat.seatId);
      const size = seat.loadout.length;
      const pending = seat.lastSpinPendingItem;
      const prevSize = prevSizeMap.get(seat.seatId);
      const prevPending = prevPendingMap.get(seat.seatId);
      const firstObservation = prevSize === undefined;
      prevSizeMap.set(seat.seatId, size);
      prevPendingMap.set(seat.seatId, pending);
      if (firstObservation) continue;

      const grew = size > (prevSize ?? size);
      const pendingAppeared = pending !== null && pending !== prevPending;
      if (!grew && !pendingAppeared) continue;

      const isSelfSeat =
        selfSeatId !== null && seat.seatId === selfSeatId;
      const nowMs = Date.now();

      // The dropped item is either the freshly appended slot (loadout
      // grew) or the pending claim (loadout was at the cap so the
      // server stashed it on lastSpinPendingItem). Both reveal the
      // same way — cycle-and-settle on the rolled cell.
      const droppedItem = grew
        ? seat.loadout[seat.loadout.length - 1] ?? null
        : pending;

      if (
        isSelfSeat &&
        seat.lastSpinItemCell !== null &&
        seat.lastSpinItemCell !== undefined &&
        slotRect &&
        droppedItem
      ) {
        const scene = slotSceneRef.current;
        const finalDef = scene ? itemDefById.get(droppedItem) : null;
        if (scene && finalDef) {
          const cell = seat.lastSpinItemCell;
          // Pick 10 random items (excluding the dropped one) for the
          // cycle phase. Producer brief 2026-05-13: 3 was too few —
          // bump to 10 so the cycle reads as a slot-style roulette
          // rather than a quick blink.
          const cyclePool = ALL_ITEMS.filter((id) => id !== droppedItem);
          const randomCycleDefs: SlotSymbolDef[] = [];
          for (let i = 0; i < 10 && cyclePool.length > 0; i++) {
            const pick = Math.floor(Math.random() * cyclePool.length);
            const pickedId = cyclePool.splice(pick, 1)[0]!;
            const def = itemDefById.get(pickedId);
            if (def) randomCycleDefs.push(def);
          }
          // capture promise; bump the strip's "visible" state only
          // AFTER the engine reveal completes so the slot tile
          // doesn't fill ahead of the on-reel animation.
          scene
            .playItemDropReveal(cell, finalDef, randomCycleDefs)
            .then(() => {
              if (grew) {
                setRevealedSelfLoadoutCount(size);
              } else {
                setRevealedSelfPendingItem(pending);
              }
            });
          continue;
        }
      }

      // Fallback (bots, or self with no item-cell info / no scene):
      // small star burst at the seat's avatar. Note: for the bot
      // branch we still need to bump revealedSelfLoadoutCount on the
      // own seat in case the engine isn't available — but bot drops
      // are not the local player so revealed state stays self-only.
      if (!isSelfSeat) {
        const avatarEl = seatRefs.current.get(seat.seatId);
        if (!avatarEl) continue;
        const aRect = avatarEl.getBoundingClientRect();
        const aCentre = {
          x: aRect.left + aRect.width / 2,
          y: aRect.top + aRect.height / 2,
        };
        newItemBursts.push({
          id: `item-${seat.seatId}-${nowMs}-${Math.random().toString(36).slice(2, 7)}`,
          startX: aCentre.x,
          startY: aCentre.y,
          endX: aCentre.x,
          endY: aCentre.y,
          bornMs: nowMs,
        });
      } else if (grew) {
        // Self-seat without scene/cell info — bump immediately so
        // the strip still updates (graceful degradation).
        setRevealedSelfLoadoutCount(size);
      } else if (pendingAppeared) {
        setRevealedSelfPendingItem(pending);
      }
    }

    for (const seatId of [...prevSizeMap.keys()]) {
      if (!seenSeatIds.has(seatId)) {
        prevSizeMap.delete(seatId);
        prevPendingMap.delete(seatId);
      }
    }

    if (newItemBursts.length > 0) {
      setItemBursts((b) => [...b, ...newItemBursts]);
      const ids = newItemBursts.map((b) => b.id);
      window.setTimeout(() => {
        setItemBursts((all) => all.filter((b) => !ids.includes(b.id)));
      }, 1500);
    }
    // Self-seat chip-fly is scheduled inside the engine-reveal
    // promise's {@code .then} so it fires AFTER the cycle settles,
    // not on SSE arrival (when the reels are still spinning). The
    // {@code newSelfReveals} array is therefore unused here; the
    // local block above pushes directly via {@code setSelfItemReveals}
    // when the promise resolves.
  }, [state.seats, selfSeatId, itemCycleDefs, itemDefById]);

  // Keep the revealed-loadout counter and pending item in sync when
  // the server clears state out-of-band (CLAIM_ITEM swap, phase
  // transition, new match). Server is authoritative; if it says the
  // loadout shrank or pending is null, the strip follows immediately
  // — only the GROWTH direction is gated on the reveal animation.
  useEffect(() => {
    if (selfSeat === null) {
      if (revealedSelfLoadoutCount !== 0) setRevealedSelfLoadoutCount(0);
      if (revealedSelfPendingItem !== null) setRevealedSelfPendingItem(null);
      return;
    }
    const actualLen = selfSeat.loadout.length;
    if (revealedSelfLoadoutCount > actualLen) {
      setRevealedSelfLoadoutCount(actualLen);
    }
    if (
      revealedSelfPendingItem !== null &&
      selfSeat.lastSpinPendingItem === null
    ) {
      setRevealedSelfPendingItem(null);
    }
  }, [
    selfSeat,
    revealedSelfLoadoutCount,
    revealedSelfPendingItem,
  ]);

  // Phase-tracker: re-baseline the revealed strip state whenever
  // REEL_RACE is freshly entered. Producer brief 2026-05-14: each
  // round must start clean — no pending-claim badge bleeding through
  // from a prior round. Also wipes the prev-{size,pending} maps so
  // the drop-detection effect treats the new round's first SSE delta
  // as a snapshot (no spurious reveal animation fires for items the
  // server happens to be carrying through).
  const prevPhaseRef = useRef<typeof state.phase | null>(null);
  useEffect(() => {
    const prevPhase = prevPhaseRef.current;
    prevPhaseRef.current = state.phase;
    if (state.phase !== 'REEL_RACE') return;
    if (prevPhase === 'REEL_RACE') return; // already inside the phase
    // Fresh entry into REEL_RACE — reset both prev-observation maps
    // so the drop-detection effect re-baselines from server truth.
    prevLoadoutSizeBySeatRef.current.clear();
    prevPendingBySeatRef.current.clear();
    setRevealedSelfLoadoutCount(selfSeat ? selfSeat.loadout.length : 0);
    setRevealedSelfPendingItem(
      selfSeat ? selfSeat.lastSpinPendingItem : null,
    );
  }, [state.phase, selfSeat]);

  // If the player chooses to spin instead of claiming, drop the
  // pending badge immediately. Producer brief: "if they choose to
  // spin on, then the opportunity to replace a symbol should
  // disappear." The strip itself already hides while isSpinning is
  // true; clearing here also prevents the badge reappearing if the
  // next spin happens to roll a fresh pending claim while we're
  // still showing the previous one's reveal.
  useEffect(() => {
    if (isSpinning && revealedSelfPendingItem !== null) {
      setRevealedSelfPendingItem(null);
    }
  }, [isSpinning, revealedSelfPendingItem]);

  // The legacy gold-chip overlay that previously popped over each
  // bonus cell on SSE state change has been removed: the slot scene
  // now renders the commissioned forest:bonus icon directly in the
  // grid cell (M1.5j) and runs a light scale-pulse on every bonus
  // cell when the spin lands (via SlotMachine.playBonusCollect),
  // distinct from the heavy per-symbol win-celebration video that
  // fires for actual payline wins.

  return (
    <div
      style={{
        position: 'relative',
        width: '100%',
        height: '100%',
        overflow: 'hidden',
        background: '#0B1838',
        color: '#E9F0FF',
        fontFamily: '"Fredoka", -apple-system, system-ui',
      }}
    >
      {/* Slot scene = the page background. Fills the viewport so the
          commissioned forest art (and the other 4 biomes when wired)
          becomes the wallpaper for the multiplayer Reel Race. The
          leaderboard + countdown overlay floats on top via z-index. */}
      <div
        ref={canvasWrapRef}
        style={{
          position: 'absolute',
          inset: 0,
          zIndex: 0,
        }}
      >
        {theme && profile ? (
          <CanvasHost
            scene="slot"
            theme={theme}
            profile={profile}
            slotSpinFn={trackedSlotSpinFn}
            onSlotSpinFired={trackedSlotSpinFn ? null : onSlotSpinFired}
            onSlotSceneReady={(instance) => {
              slotSceneRef.current = instance;
            }}
            onSlotLayoutBounds={handleSlotLayoutBounds}
            onboardingSession={onboardingSession}
            // Multiplayer Reel Race: no gamble offer. Wins collect
            // immediately so the race-to-collect rhythm isn't broken
            // by the higher/lower interrupt.
            gambleEnabled={false}
            onOpenSettings={onOpenSettings ?? undefined}
            showLoadoutStrip
            loadoutSlots={loadoutSlotAliases}
            loadoutPendingIcon={
              revealedSelfPendingItem ? `item:${revealedSelfPendingItem}` : null
            }
            loadoutPendingLabel={
              revealedSelfPendingItem ? ITEMS[revealedSelfPendingItem].name : ''
            }
            loadoutHidden={stripHidden}
            onLoadoutTileClick={handleClaimItemTile}
            onInfoModalToggle={setInfoModalOpen}
          />
        ) : (
          <FallbackReelGrid />
        )}
        {/* Reel-skin selection moved into the Settings dialog (a simple
            dropdown, multiplayer-only) — producer 2026-06-08. The old
            top-left HUD pill lived here. */}
      </div>

      {/* Countdown — sits ABOVE the slot frame's top edge, between the
          BalancePill (anchored at top:14 in the lobby with the bonus
          pot + items on its left side) and the slot's painted wood.
          Far enough down that the pill reads first; the countdown
          is the secondary beat.

          Producer brief 2026-05-15: hidden while the slot's info
          modal is open so the canvas-rendered paytable / items
          dialog reads cleanly. The canvas sits at zIndex 0; this
          overlay at zIndex 10 would otherwise paint over the
          dialog. */}
      <div
        style={{
          position: 'absolute',
          // Desktop: anchor just above the slot frame's painted top edge
          // (machineTop, live from the layout) so the countdown clears the
          // reels. The desktop 62% slot sits lower than the phone full-width
          // slot, and the old fixed top:120 dropped it onto the frame
          // (producer 2026-06-10). Phones keep the fixed offset (their
          // layout + safe-area unchanged). The wallet pill is top-RIGHT and
          // this countdown is centred, so they never collide; the floor just
          // keeps it from sliding off the top edge on a tall, narrow desktop.
          top:
            slotBounds && slotBounds.rendererWidth >= 640
              ? Math.max(16, slotBounds.machineTop - 56)
              : 120,
          left: 0,
          right: 0,
          zIndex: 10,
          textAlign: 'center',
          pointerEvents: 'none',
          visibility: infoModalOpen ? 'hidden' : 'visible',
        }}
      >
        <div
          style={{
            fontSize: 30,
            fontWeight: 800,
            color: remainingSec <= 10 ? '#FF6F6F' : '#FFFFFF',
            fontVariantNumeric: 'tabular-nums',
            textShadow: '0 2px 6px rgba(0,0,0,0.85)',
            lineHeight: 1,
          }}
        >
          0:{remainingSec.toString().padStart(2, '0')}
        </div>
        {state.matchNumber !== null ? (
          <div
            style={{
              marginTop: 4,
              fontSize: 12,
              opacity: 0.85,
              textShadow: '0 1px 2px rgba(0,0,0,0.85)',
              letterSpacing: 0.6,
            }}
          >
            Match {state.matchNumber} of {WHEEL_MATCHES_PER_SESSION}
          </div>
        ) : null}
      </div>

      {/* Avatar rail. Two layouts:
            – Mobile (viewport ≤ {@link MOBILE_RAIL_BREAKPOINT_PX}px):
              anchored to the bottom of the viewport, 4 columns × 2
              rows, left-to-right by rank (1..4 on row 1, 5..6 +
              invite on row 2). Producer brief 2026-05-11: "on mobile
              the player avatars need to be at the bottom on two rows
              … otherwise they are blocking the reels."
            – Tablet+ (wider viewports): the classic right-side
              vertical stack — the slot frame doesn't span the full
              width, so the lateral rail has room and the leaderboard
              reads top-to-bottom as a podium.
          Each seat is absolutely positioned by its current rank's
          (col,row)/(top) so rank changes animate via CSS transitions
          (FLIP-style slide) instead of jumping. */}
      {isMobileRail ? (
        <ol
          style={{
            position: 'absolute',
            // Lifted clear of the reaction tray (anchored at bottom:18,
            // ~44px tall) so the emoji pill no longer covers the lower
            // seat row (ranks 5–6). Producer flag 2026-05-29.
            bottom: MOBILE_RAIL_BOTTOM_INSET,
            left: '50%',
            // Scale about the bottom-centre so the bottom edge stays put
            // (clearing the reaction pill) while the top row drops clear
            // of the SPIN disc on short viewports. {@link railScale} is 1
            // on tall viewports, so this is a no-op there.
            transform: `translateX(-50%) scale(${railScale})`,
            transformOrigin: '50% 100%',
            zIndex: 10,
            margin: 0,
            padding: 0,
            listStyle: 'none',
            width:
              MOBILE_RAIL_COLS * AVATAR_DIAMETER +
              (MOBILE_RAIL_COLS - 1) *
                (MOBILE_RAIL_PITCH_X - AVATAR_DIAMETER),
            height: 2 * MOBILE_RAIL_PITCH_Y,
            pointerEvents: 'none',
            visibility: infoModalOpen ? 'hidden' : 'visible',
          }}
        >
          {leaderboard.map((seat, idx) => {
            const col = idx % MOBILE_RAIL_COLS;
            const row = Math.floor(idx / MOBILE_RAIL_COLS);
            return (
              <SeatAvatarBadge
                key={seat.seatId}
                seat={seat}
                rank={idx + 1}
                left={col * MOBILE_RAIL_PITCH_X}
                top={row * MOBILE_RAIL_PITCH_Y}
                isSelf={selfSeatId !== null && seat.seatId === selfSeatId}
                registerRef={(el) => {
                  if (el) seatRefs.current.set(seat.seatId, el);
                  else seatRefs.current.delete(seat.seatId);
                }}
                reactions={reactions}
                onArchetypeTap={onArchetypeTap}
              />
            );
          })}
          <li
            style={{
              position: 'absolute',
              left:
                (leaderboard.length % MOBILE_RAIL_COLS) *
                MOBILE_RAIL_PITCH_X,
              top:
                Math.floor(leaderboard.length / MOBILE_RAIL_COLS) *
                MOBILE_RAIL_PITCH_Y,
              pointerEvents: 'auto',
            }}
          >
            <InviteAvatarButton inviteCode={inviteCode} />
          </li>
        </ol>
      ) : (
        <ol
          style={{
            position: 'absolute',
            top: 90,
            right: 18,
            zIndex: 10,
            margin: 0,
            padding: 0,
            listStyle: 'none',
            width: AVATAR_DIAMETER,
            height:
              leaderboard.length * SEAT_RAIL_PITCH +
              INVITE_SLOT_OFFSET +
              AVATAR_DIAMETER,
            visibility: infoModalOpen ? 'hidden' : 'visible',
          }}
        >
          {leaderboard.map((seat, idx) => (
            <SeatAvatarBadge
              key={seat.seatId}
              seat={seat}
              rank={idx + 1}
              left={0}
              top={idx * SEAT_RAIL_PITCH}
              isSelf={selfSeatId !== null && seat.seatId === selfSeatId}
              registerRef={(el) => {
                if (el) seatRefs.current.set(seat.seatId, el);
                else seatRefs.current.delete(seat.seatId);
              }}
              reactions={reactions}
              onArchetypeTap={onArchetypeTap}
              gestureSlot={
                seat.kind !== 'EMPTY' && seat.seatId !== selfSeatId ? (
                  <SeatGestureButton
                    targetSeatId={seat.seatId}
                    roomId={roomId}
                    playerId={playerId}
                  />
                ) : null
              }
            />
          ))}
          <li
            style={{
              position: 'absolute',
              top:
                leaderboard.length * SEAT_RAIL_PITCH + INVITE_SLOT_OFFSET,
              left: 0,
            }}
          >
            <InviteAvatarButton inviteCode={inviteCode} />
          </li>
        </ol>
      )}

      {/* Reaction tray moved to the room level (WheelLobbyView) so it
          sits in one consistent spot across every phase. Was here at
          bottom-left, which fought the seat rail. */}

      {/* Collect-burst overlay — fixed-position layer above the slot
          canvas + below dialogs. Each burst animates from the slot
          centre to its target avatar. */}
      <div
        aria-hidden
        style={{
          position: 'fixed',
          inset: 0,
          pointerEvents: 'none',
          zIndex: 12,
        }}
      >
        {bursts.map((b) => (
          <BurstParticle key={b.id} burst={b} />
        ))}
        {itemBursts.map((b) => (
          <ItemBurstParticle key={b.id} burst={b} iconUrl={bonusItemUrl} />
        ))}
      </div>

      {/* Loadout strip rendered inside the slot HUD via Pixi (see
          LoadoutStripHud + SlotHudComponent). State below is pushed
          through CanvasHost props to the slot scene's setters. */}

      {/* Floating Spin button (overlay) when the parent supplies onSpin —
          hidden in normal play (the slot HUD owns spin); shown when the
          page is rendered in audience-mode-without-canvas variants. */}
      {onSpin ? (
        <button
          type="button"
          disabled={spinPending}
          onClick={() => {
            if (spinPending) return;
            setSpinPending(true);
            void onSpin().finally(() => {
              window.setTimeout(() => setSpinPending(false), 250);
            });
          }}
          style={{
            position: 'absolute',
            bottom: 20,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 11,
            padding: '12px 32px',
            background: spinPending
              ? 'rgba(255, 201, 64, 0.4)'
              : 'linear-gradient(180deg, #FFC940 0%, #E89E1A 100%)',
            color: '#1A1304',
            fontFamily: 'inherit',
            fontWeight: 800,
            fontSize: 14,
            letterSpacing: 1.2,
            textTransform: 'uppercase',
            border: 'none',
            borderRadius: 12,
            cursor: spinPending ? 'wait' : 'pointer',
            boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
          }}
        >
          {spinPending ? 'Spinning…' : '🎰 Spin the reels'}
        </button>
      ) : null}

    </div>
  );
}

// ---- subcomponents --------------------------------------------------------

const AVATAR_DIAMETER = 56;



function SeatAvatarBadge({
  seat,
  rank,
  left,
  top,
  isSelf,
  registerRef,
  reactions,
  onArchetypeTap,
  gestureSlot,
}: {
  seat: WheelSeatView;
  rank: number;
  /** Computed absolute `left` + `top` offsets (px) within the rail.
   * Both animated via CSS transition so rank changes slide rather
   * than jump when the leaderboard re-sorts. */
  left: number;
  top: number;
  isSelf: boolean;
  /** Tells the parent the rendered <li>'s DOM node so it can be
   * targeted by collect-burst animations. Called with null on
   * unmount. */
  registerRef?: (el: HTMLLIElement | null) => void;
  /** R1.4 — live ring buffer of reactions; ReactionFloat filters by
   *  this seat's id and renders float-up overlays for matching ones. */
  reactions?: ReadonlyArray<ReceivedReaction>;
  /** U11 cast favourites — when set AND this seat has an archetype
   *  (i.e. a bot), the avatar is wrapped in a button that opens the
   *  ProfileCard for that archetype. Player seats and EMPTY seats stay
   *  inert (the badge still renders but isn't tappable). */
  onArchetypeTap?: ((archetype: BotArchetype) => void) | null;
  /** Quick-gesture button overlay, built by the parent (which holds the
   *  room context). Pinned bottom-left of the avatar; the parent omits it
   *  for the local player's own seat + EMPTY seats. */
  gestureSlot?: ReactNode;
}) {
  const ringColor = isSelf
    ? '#FFC940'
    : rank === 1
      ? '#FFC940'
      : 'rgba(255,255,255,0.18)';
  const rankBadgeBg = rank === 1 ? '#FFC940' : '#1B2C56';
  const rankBadgeColor = rank === 1 ? '#1A1304' : '#FFFFFF';

  return (
    <li
      ref={registerRef}
      title={`${seat.displayName} · rank ${rank} · ${seat.tokens} tokens`}
      style={{
        position: 'absolute',
        top,
        left,
        width: AVATAR_DIAMETER,
        height: AVATAR_DIAMETER,
        transition:
          'top 380ms cubic-bezier(0.2, 0.7, 0.4, 1), left 380ms cubic-bezier(0.2, 0.7, 0.4, 1)',
        willChange: 'top, left',
        pointerEvents: 'auto',
      }}
    >
      {/* Avatar — round, with a coloured ring for self / leader.
          U11: when onArchetypeTap is set and the seat carries an
          archetype (i.e. it's a bot), wrap in a button so tapping the
          avatar opens the ProfileCard for that archetype. Player and
          EMPTY seats render the inert <div> form. */}
      {onArchetypeTap !== null &&
      onArchetypeTap !== undefined &&
      seat.archetype !== null ? (
        <button
          type="button"
          onClick={() =>
            onArchetypeTap(seat.archetype as BotArchetype)
          }
          aria-label={`Open profile for ${seat.displayName}`}
          style={{
            width: AVATAR_DIAMETER,
            height: AVATAR_DIAMETER,
            padding: 0,
            border: 'none',
            borderRadius: '50%',
            overflow: 'hidden',
            cursor: 'pointer',
            boxShadow:
              `0 0 0 2px ${ringColor}, 0 4px 10px rgba(0, 0, 0, 0.45)`,
            background: '#0B1838',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <SeatAvatar seat={seat} size={AVATAR_DIAMETER} />
        </button>
      ) : (
        <div
          style={{
            width: AVATAR_DIAMETER,
            height: AVATAR_DIAMETER,
            borderRadius: '50%',
            overflow: 'hidden',
            boxShadow:
              `0 0 0 2px ${ringColor}, 0 4px 10px rgba(0, 0, 0, 0.45)`,
            background: '#0B1838',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {seat.kind === 'EMPTY' ? (
            <div
              style={{
                width: AVATAR_DIAMETER,
                height: AVATAR_DIAMETER,
                background: 'rgba(255,255,255,0.05)',
              }}
            />
          ) : (
            <SeatAvatar seat={seat} size={AVATAR_DIAMETER} />
          )}
        </div>
      )}

      {/* Position badge — top-left, small chip showing rank number. */}
      <div
        aria-label={`rank ${rank}`}
        style={{
          position: 'absolute',
          top: -4,
          left: -4,
          width: 22,
          height: 22,
          borderRadius: '50%',
          background: rankBadgeBg,
          color: rankBadgeColor,
          fontSize: 12,
          fontWeight: 800,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.45)',
          border: '1.5px solid #060d22',
        }}
      >
        {rank}
      </div>

      {/* Token-count badge — bottom-right, coin chip. */}
      <div
        aria-label={`${seat.tokens} tokens`}
        style={{
          position: 'absolute',
          bottom: -6,
          right: -10,
          minWidth: 30,
          height: 22,
          padding: '0 7px',
          borderRadius: 11,
          background: 'rgba(6, 13, 34, 0.95)',
          color: '#FFC940',
          fontSize: 11,
          fontWeight: 800,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 3,
          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.45)',
          border: '1.5px solid #060d22',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        <Icon name="coin" size={11} alt="" />
        {seat.tokens}
      </div>

      {/* R1.4 — float-up reaction overlay. Anchored to the <li> bottom
          via {@code position: absolute; bottom: 50%}; only renders
          while reactions for THIS seat are within their animation
          lifetime. Pointer-events:none so it never blocks taps. */}
      {reactions ? (
        <ReactionFloat reactions={reactions} seatId={seat.seatId} />
      ) : null}
      {gestureSlot}
    </li>
  );
}

/** `+N` pop anchored to the player's avatar. Pops in at full
 *  opacity, floats up ~50px while fading out over ~900ms. */
function BurstParticle({ burst }: { burst: CollectBurst }) {
  const [drifted, setDrifted] = useState(false);

  useEffect(() => {
    // Two rAFs so the entry transform commits before the drift
    // transition fires.
    const r1 = window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => setDrifted(true));
    });
    return () => window.cancelAnimationFrame(r1);
  }, []);

  const drift = drifted ? -56 : 0;
  const opacity = drifted ? 0 : 1;
  const scale = drifted ? 1.05 : 1.4;

  return (
    <div
      style={{
        position: 'fixed',
        left: 0,
        top: 0,
        transform: `translate(${burst.endX}px, ${burst.endY + drift}px) translate(-50%, -50%) scale(${scale})`,
        transition:
          'transform 900ms cubic-bezier(0.2, 0.7, 0.4, 1), opacity 900ms ease-out',
        opacity,
        fontSize: 22,
        fontWeight: 900,
        letterSpacing: 0.6,
        color: '#FFC940',
        textShadow: '0 2px 6px rgba(0,0,0,0.85)',
        fontVariantNumeric: 'tabular-nums',
        willChange: 'transform, opacity',
      }}
    >
      +{burst.delta}
    </div>
  );
}

/** "+ITEM" pop anchored to the player's avatar when a Reel-Race
 *  spin granted an item drop. Holds slightly longer than the +N
 *  token burst so the more memorable beat stays on screen long
 *  enough for the player to register what they got. */
/** Bot-side item-drop indicator. Small star that pops near the
 *  bot's avatar so the player can see another seat grabbed an item.
 *  Self drops use {@link SelfItemDropReveal} instead — that's the
 *  full reels → linger → reveal → fly-to-chip beat. */
function ItemBurstParticle({
  burst,
  iconUrl,
}: {
  burst: ItemBurst;
  iconUrl: string;
}) {
  const [drifted, setDrifted] = useState(false);
  useEffect(() => {
    const r1 = window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => setDrifted(true));
    });
    return () => window.cancelAnimationFrame(r1);
  }, []);

  const drift = drifted ? -28 : 0;
  const opacity = drifted ? 0 : 1;
  const scale = drifted ? 1.05 : 1.4;
  return (
    <div
      style={{
        position: 'fixed',
        left: 0,
        top: 0,
        transform: `translate(${burst.endX}px, ${burst.endY + drift}px) translate(-50%, -50%) scale(${scale})`,
        transition:
          'transform 1300ms cubic-bezier(0.2, 0.7, 0.4, 1), opacity 1300ms ease-out',
        opacity,
        width: 32,
        height: 32,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        pointerEvents: 'none',
        willChange: 'transform, opacity',
      }}
    >
      <img
        src={iconUrl}
        alt=""
        aria-hidden
        draggable={false}
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'contain',
          filter:
            'drop-shadow(0 0 6px rgba(255,228,138,0.9)) drop-shadow(0 0 12px rgba(255,201,64,0.7))',
        }}
      />
    </div>
  );
}

/** Mobile-first cell-position estimate. The slot frame is centred
 *  inside the canvas with margins; reels span ~86% of the canvas
 *  width and the visible grid sits in the upper-middle band. These
 *  fractions are approximate (the engine doesn't expose its layout
 *  yet) but the drift is a few px — invisible at the scale of the
 *  reveal star (~52px). Adjust here if the slot frame ever shifts. */
const REELS_LEFT_FRAC = 0.07;
const REELS_RIGHT_FRAC = 0.93;
const REELS_TOP_FRAC = 0.31;
const REELS_BOTTOM_FRAC = 0.55;

/** Self-seat item-drop icon-fly. The engine handles the on-reel
 *  pulse + cycle + settle inside the cell via
 *  {@code SlotDemoScene.playItemDropReveal}; this component only
 *  handles the final beat — the settled item's icon arcs from the
 *  cell to the BalancePill's items chip so the player sees the
 *  item "received" handoff. Mounting is scheduled inside the engine
 *  reveal's promise resolution, so by the time this renders the
 *  cell is already settled on the dropped item — no CSS delay
 *  needed before the chip animates. */
function SelfItemDropReveal({
  reveal,
}: {
  reveal: ItemDropReveal;
}) {
  const { canvasRect, cell, itemId, endX, endY } = reveal;
  const col = cell % 5;
  const row = Math.floor(cell / 5);
  // Cell-centre, as a fraction of the canvas.
  const cellFracX =
    REELS_LEFT_FRAC + ((col + 0.5) / 5) * (REELS_RIGHT_FRAC - REELS_LEFT_FRAC);
  const cellFracY =
    REELS_TOP_FRAC + ((row + 0.5) / 3) * (REELS_BOTTOM_FRAC - REELS_TOP_FRAC);
  const cellX = canvasRect.left + cellFracX * canvasRect.width;
  const cellY = canvasRect.top + cellFracY * canvasRect.height;
  // Arc travel: dx/dy from cell → chip drives the keyframed end-state.
  const travelX = endX - cellX;
  const travelY = endY - cellY;
  const reveakKey = reveal.id.replace(/[^a-zA-Z0-9_-]/g, '');

  const iconUrl = ITEM_ICON_URL_BY_ID[itemId] ?? null;

  return (
    <>
      {/* The settled item's icon arcs from the cell to the BalancePill
          items chip — producer brief 2026-05-13: "the item symbol that
          flies to the hud, not the label." Spawned by the engine
          reveal's promise resolution so the cell is already settled
          on this exact item when the fly starts; the player sees
          their item lift off the reel and join their inventory. */}
      {iconUrl ? (
        <img
          src={iconUrl}
          alt=""
          aria-hidden
          draggable={false}
          style={{
            position: 'fixed',
            left: cellX,
            top: cellY,
            width: 64,
            height: 64,
            objectFit: 'contain',
            transform: 'translate(-50%, -50%) scale(0.4)',
            opacity: 0,
            pointerEvents: 'none',
            filter:
              'drop-shadow(0 0 12px rgba(255,228,138,0.85)) drop-shadow(0 6px 12px rgba(0,0,0,0.55))',
            animation: `gtItemReveal-chip-${reveakKey} 900ms cubic-bezier(0.4, 0.0, 0.2, 1) forwards`,
            willChange: 'transform, opacity',
          }}
        />
      ) : null}
      <style>{`
        @keyframes gtItemReveal-chip-${reveakKey} {
          0%   { opacity: 0; transform: translate(-50%, -50%) scale(0.4); }
          20%  { opacity: 1; transform: translate(-50%, -50%) scale(1.15); }
          35%  { opacity: 1; transform: translate(-50%, -50%) scale(1); }
          100% {
            opacity: 0;
            transform:
              translate(calc(-50% + ${travelX}px), calc(-50% + ${travelY}px))
              scale(0.45);
          }
        }
      `}</style>
    </>
  );
}

/** Seat-rail "+" — invite a friend into the room. Tapping opens the native
 *  share sheet with a tap-able link (goodtime.casino/r/GTC-XXXX) so the host
 *  can send it to a friend, who lands straight on the table. Falls back to
 *  copying the link when the Web Share API is unavailable. Without a code
 *  it's an inert placeholder (a brief press-flash).
 *
 *  <p>Styled as a bright pink button with a pulsing ring (producer, 2026-10-06:
 *  "make the invite button more prominent"). It was a dim dashed circle that
 *  read as an empty seat, and no outside player had used it since late
 *  September. The pink matches the SPIN button so it reads as an action. */
function InviteAvatarButton({ inviteCode = null }: { inviteCode?: string | null }) {
  const [pressed, setPressed] = useState(false);
  const [open, setOpen] = useState(false);
  const handleClick = (): void => {
    setPressed(true);
    window.setTimeout(() => setPressed(false), 600);
    // Open the explainer dialogue rather than firing the OS share sheet
    // straight away (producer contextual-explainer brief). The actual
    // share / clipboard-copy happens from the sheet's CTA.
    setOpen(true);
  };
  return (
    <>
    <style>{`
      @keyframes gtInvitePulse {
        0% { box-shadow: 0 0 0 0 rgba(255, 63, 164, 0.75), 0 4px 12px rgba(0, 0, 0, 0.5); }
        70% { box-shadow: 0 0 0 14px rgba(255, 63, 164, 0), 0 4px 12px rgba(0, 0, 0, 0.5); }
        100% { box-shadow: 0 0 0 0 rgba(255, 63, 164, 0), 0 4px 12px rgba(0, 0, 0, 0.5); }
      }
      @media (prefers-reduced-motion: reduce) {
        .gt-invite-seat { animation: none !important; }
      }
    `}</style>
    <button
      type="button"
      className="gt-invite-seat"
      title="Invite a friend to your table"
      aria-label="Invite friends"
      onClick={handleClick}
      style={{
        position: 'relative',
        width: AVATAR_DIAMETER,
        height: AVATAR_DIAMETER,
        borderRadius: '50%',
        background:
          'radial-gradient(circle at 35% 30%, #FF8AD0 0%, #FF3FA4 48%, #C2187A 100%)',
        border: '3px solid #FFE48A',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#FFFFFF',
        fontFamily: 'inherit',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.5)',
        animation: 'gtInvitePulse 1.8s ease-out infinite',
        marginTop: 4,
        padding: 0,
        transition: 'transform 0.1s',
        transform: pressed ? 'scale(0.92)' : 'scale(1)',
      }}
    >
      <span
        style={{
          fontSize: 32,
          fontWeight: 900,
          lineHeight: 1,
          marginTop: -3,
          textShadow: '0 2px 3px rgba(120, 0, 60, 0.6)',
        }}
        aria-hidden
      >
        +
      </span>
      <span
        style={{
          position: 'absolute',
          bottom: -24,
          left: '50%',
          transform: 'translateX(-50%)',
          fontSize: 12,
          fontWeight: 900,
          letterSpacing: 1,
          textTransform: 'uppercase',
          color: '#FFE48A',
          textShadow: '0 1px 3px rgba(0,0,0,0.9)',
          whiteSpace: 'nowrap',
        }}
      >
        Invite
      </span>
    </button>
      {open ? (
        <InviteFriendSheet
          inviteCode={inviteCode}
          onClose={() => setOpen(false)}
        />
      ) : null}
    </>
  );
}

/** Fallback when no theme/profile is supplied — keeps the slot space
 *  filled with a static "loading" visual rather than collapsing the
 *  layout. Tests render this; production paths always have a theme. */
function FallbackReelGrid() {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(5, 1fr)',
        gap: 4,
        padding: 8,
        background:
          'linear-gradient(180deg, rgba(46, 76, 58, 0.4) 0%, rgba(20, 38, 28, 0.6) 100%)',
        borderRadius: 14,
        border: '1px solid rgba(122, 167, 110, 0.25)',
        opacity: 0.5,
      }}
    >
      {Array.from({ length: 15 }, (_, i) => (
        <div
          key={i}
          style={{
            aspectRatio: '1',
            background: 'rgba(20, 35, 28, 0.65)',
            border: '1px solid rgba(255,255,255,0.06)',
            borderRadius: 10,
          }}
        />
      ))}
    </div>
  );
}

// ---- helpers --------------------------------------------------------------

function sortByTokens(
  seats: ReadonlyArray<WheelSeatView>,
): ReadonlyArray<WheelSeatView> {
  // Stable sort: primary by tokens DESC, tiebreaker seatId ASC.
  return [...seats].sort((a, b) => {
    if (b.tokens !== a.tokens) return b.tokens - a.tokens;
    return a.seatId - b.seatId;
  });
}
